import {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { MdSearch } from 'react-icons/md'
import { useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'
import * as Yup from 'yup'

import Autocomplete from 'shared/components/Autocomplete'
import Input from 'shared/components/Input'
import SnackBar from 'shared/components/SnackBar'
import useDebounce from 'shared/hooks/useDebounce'
import ICidade from 'shared/modules/cidades/domain/entities/Cidade'
import IFindByNomeImpl from 'shared/modules/cidades/domain/usecases/FindByNome'
import ContainerPage from 'shared/components/ContainerPage'
import Form from 'shared/components/Form'

import { useFiltros } from '../../hooks/filtros'

type TProps = {
  findByNome: IFindByNomeImpl
}

const Filtros: FunctionComponent<TProps> = ({ findByNome }) => {
  const {
    origemCompleta,
    destinoCompleto,
    setDestinoCompleto,
    setOrigemCompleta,
    nomeOuCpf,
    cte,
    setCte,
    placa,
    setNomeOuCpf,
    setPlaca,
  } = useFiltros()
  const [loading, setLoading] = useState(false)
  const [placaFiltro, setPlacaFiltro] = useState(placa)
  const [nomeOuCpfFiltro, setNomeOuCpfFiltro] = useState(nomeOuCpf)
  const [cteFiltro, setCteFiltro] = useState(cte)
  const [cidadesOrigem, setCidadesOrigem] = useState<ICidade[]>([])
  const [cidadesDestino, setCidadesDestino] = useState<ICidade[]>([])
  const [origemInput, setOrigemInput] = useState('')
  const [destinoInput, setDestinoInput] = useState('')
  const debouncedOrigem = useDebounce(origemInput, 1000)
  const debouncedDestino = useDebounce(destinoInput, 1000)
  const [origemAutocomplete, setOrigemAutocomplete] = useState<ICidade>(
    origemCompleta || ({} as ICidade),
  )
  const [destinoAutocomplete, setDestinoAutocomplete] = useState<ICidade>(
    destinoCompleto || ({} as ICidade),
  )
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState('')
  const history = useHistory()

  useEffect(() => {
    const init = async () => {
      if (
        debouncedOrigem &&
        origemInput !==
          `${origemAutocomplete?.nome} - ${origemAutocomplete?.uf_estado}`
      ) {
        setLoading(true)
        try {
          const response = await findByNome.execute(debouncedOrigem)
          setLoading(false)
          setCidadesOrigem(response.cidades)
        } catch (error) {
          setMessages(
            'Não foi possível carregar as cidades, por favor tente novamente em alguns minutos.',
          )
          setOpen(true)
          setLoading(false)
        }
      } else {
        setCidadesOrigem([])
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrigem, findByNome])

  useEffect(() => {
    const init = async () => {
      if (
        debouncedDestino &&
        destinoInput !==
          `${destinoAutocomplete?.nome} - ${destinoAutocomplete?.uf_estado}`
      ) {
        setLoading(true)
        try {
          const response = await findByNome.execute(debouncedDestino)
          setLoading(false)

          setCidadesDestino(response.cidades)
        } catch (error) {
          setMessages(
            'Não foi possível carregar as cidades, por favor tente novamente em alguns minutos.',
          )
          setOpen(true)
          setLoading(false)
        }
      } else {
        setCidadesDestino([])
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDestino, findByNome])

  const handleSubmit = useCallback(() => {
    if (origemAutocomplete?.nome) {
      setOrigemCompleta(origemAutocomplete)
    } else {
      setOrigemCompleta({} as ICidade)
    }

    if (destinoAutocomplete?.nome) {
      setDestinoCompleto(destinoAutocomplete)
    } else {
      setDestinoCompleto({} as ICidade)
    }

    setPlaca(placaFiltro)
    setNomeOuCpf(nomeOuCpfFiltro)
    setCte(cteFiltro)

    history.push('/lotes-solicitados')
  }, [
    history,
    setOrigemCompleta,
    setDestinoCompleto,
    destinoAutocomplete,
    origemAutocomplete,
    placaFiltro,
    setPlaca,
    setNomeOuCpf,
    nomeOuCpfFiltro,
    setCte,
    cteFiltro,
  ])

  const handlePlaca = (event: ChangeEvent<HTMLInputElement>) => {
    setPlacaFiltro(event.target.value)
  }

  const handleNomeOuCpf = (event: ChangeEvent<HTMLInputElement>) => {
    setNomeOuCpfFiltro(event.target.value)
  }

  const handleCte = (event: ChangeEvent<HTMLInputElement>) => {
    setCteFiltro(event.target.value)
  }

  const handleOrigem = useCallback((cidade: string) => {
    setOrigemInput(cidade)
  }, [])

  const handleDestino = useCallback((cidade: string) => {
    setDestinoInput(cidade)
  }, [])

  return (
    <ContainerPage
      breadCrumb="Filtros"
      title="Filtrar"
      showFooterScaffold={true}
      titleNextButton="OK"
      onClickNextButton={() => handleSubmit()}
    >
      <Form
        enableReinitialize
        onSubmit={() => handleSubmit()}
        initialValues={{
          placa: placaFiltro,
          nomeOuCpf: nomeOuCpfFiltro,
          cte: cteFiltro,
        }}
        validationSchema={() => ({
          placa: Yup.string(),
          nomeOuCpf: Yup.string(),
          cte: Yup.string(),
        })}
      >
        {formControl => (
          <Grid container xs={12} spacing={6}>
            <Grid item xs={6}>
              <Input
                placeholder="Digite o nome ou o CPF"
                name="nomeOuCpf"
                formControl={formControl}
                errorMessage={''}
                onChange={handleNomeOuCpf}
                label={'Nome ou CPF'}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                placeholder="Digite a placa do cavalo"
                name="placa"
                formControl={formControl}
                errorMessage={''}
                onChange={handlePlaca}
                label={'Caminhão'}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                defaultValue={
                  origemAutocomplete?.nome ? origemAutocomplete : null
                }
                label={'Origem'}
                placeholder="Digite para buscar"
                onChange={(
                  event: ChangeEvent<HTMLInputElement>,
                  newValue: ICidade,
                ) => setOrigemAutocomplete(newValue)}
                inputValue={origemInput}
                onInputChange={(event, newInputValue) => {
                  handleOrigem(newInputValue)
                }}
                options={cidadesOrigem}
                getOptionLabel={(option: ICidade) =>
                  `${option.nome} - ${option.uf_estado}`
                }
                loading={loading}
                popupIcon={MdSearch}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                defaultValue={
                  destinoAutocomplete?.nome ? destinoAutocomplete : null
                }
                label={'Destino'}
                placeholder="Digite para buscar"
                onChange={(
                  event: ChangeEvent<HTMLInputElement>,
                  newValue: ICidade,
                ) => {
                  setDestinoAutocomplete(newValue)
                }}
                inputValue={destinoInput}
                onInputChange={(event, newInputValue) => {
                  handleDestino(newInputValue)
                }}
                options={cidadesDestino}
                getOptionLabel={(option: ICidade) =>
                  `${option.nome} - ${option.uf_estado}`
                }
                loading={loading}
                popupIcon={MdSearch}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                placeholder="Digite o CT-e"
                name="cte"
                formControl={formControl}
                errorMessage={''}
                onChange={handleCte}
                label={'CT-e'}
              />
            </Grid>
          </Grid>
        )}
      </Form>
      <SnackBar
        open={open}
        message={messages}
        setOpen={setOpen}
        severity={'error'}
      />
    </ContainerPage>
  )
}

export default Filtros
